import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import SiteLayout from "./components/site/SiteLayout";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Solutions from "./pages/Solutions";
import Industries from "./pages/Industries";
import Features from "./pages/Features";
import HowItWorks from "./pages/HowItWorks";
import Pricing from "./pages/Pricing";
import CaseStudies from "./pages/CaseStudies";
import About from "./pages/About";
import Contact from "./pages/Contact";
import BookDemo from "./pages/BookDemo";
import LegalPage from "./pages/LegalPage";
import ErrorPage from "./pages/ErrorPage";
import { Login, Signup, ForgotPassword } from "./pages/auth/AuthPages";
import AuthCallback from "./pages/auth/AuthCallback";
import AppLayout from "./components/app/AppLayout";
import Dashboard from "./pages/app/Dashboard";
import Leads from "./pages/app/Leads";
import Customers from "./pages/app/Customers";
import CustomerProfile from "./pages/app/CustomerProfile";
import { CRM, Pipeline } from "./pages/app/PipelineCRM";
import Appointments from "./pages/app/Appointments";
import Calls from "./pages/app/Calls";
import Messages from "./pages/app/Messages";
import Tasks from "./pages/app/Tasks";
import Automations from "./pages/app/Automations";
import AIAgents from "./pages/app/AIAgents";
import Analytics from "./pages/app/Analytics";
import Payments from "./pages/app/Payments";
import Team from "./pages/app/Team";
import Integrations from "./pages/app/Integrations";
import Settings from "./pages/app/Settings";
import Onboarding from "./pages/app/Onboarding";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import {
  AdminLayout, AdminOverview, AdminClients, AdminUsers, AdminBusinesses,
  AdminSubscriptions, AdminUsage, AdminSystem, AdminSettings,
} from "./components/app/AdminPortal";

const Splash = () => (
  <div data-testid="auth-splash" className="flex min-h-screen items-center justify-center bg-elv-base">
    <span className="h-6 w-6 animate-spin rounded-full border-2 border-elv-green border-t-transparent" />
  </div>
);

function Protected({ children, superadmin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (superadmin && !user.is_superadmin) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to={user.is_superadmin ? "/admin" : "/app/dashboard"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/features" element={<Features />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/book-demo" element={<BookDemo />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
      </Route>

      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />

      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="crm" element={<CRM />} />
        <Route path="leads" element={<Leads />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:customerId" element={<CustomerProfile />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="calls" element={<Calls />} />
        <Route path="messages" element={<Messages />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="automations" element={<Automations />} />
        <Route path="ai-agents" element={<AIAgents />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="payments" element={<Payments />} />
        <Route path="team" element={<Team />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="settings" element={<Settings />} />
        <Route path="onboarding" element={<Onboarding />} />
      </Route>

      <Route path="/admin" element={<Protected superadmin><AdminLayout /></Protected>}>
        <Route index element={<AdminOverview />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="businesses" element={<AdminBusinesses />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="usage" element={<AdminUsage />} />
        <Route path="system" element={<AdminSystem />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="/500" element={<ErrorPage code="500" />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<ErrorPage code="404" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#0A0F14",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#F7F9FC",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
